import React, { Component } from "react";
import { AppCard, DataTableToolbar } from "../../components/ui";
import _ from "lodash";
import { Column, ColumnFilterElementTemplateOptions } from "primereact/column";
import { dateBodyTemplate } from "../../utils/columnTemplates";
import {
    AllTransactionRequestModel,
    PaymentStatus,
    TransactionReportDetails,
    TransactionReportResponse,
    TransactionStatus,
} from "../../models/Transactions";

import TransactionDetailsDialog from "../Users/TransactionDetails";
import { dateRangeFilterTemplate, statusFilterMatchModeOptions, stringFilterMatchModeOptions } from "../Paginator";

import { FilterMatchMode } from "primereact/api";
import { toastService } from "../../services/toastService";
import {
    ActionBodyTemplate,
    RenderPaymentStatusBadge,
    RenderStatusBadge,
    TransactionSystemFilterTemplate,
    TransactionTypeFilterTemplate,
} from "./TransactionFilterTemplates";
import { transactionsReportApiService } from "../../services/api/operations/TransactionsReportApiService";
import { SelectItem } from "../../models/SelectItem";
import { Dropdown } from "primereact/dropdown";
import { SearchRequestBaseModel, SortOrderType } from "../../models/SearchRequestModel";
import { utils } from "../../utils/utils";
import ExtendedDataTable from "../../components/ExtendedDataTable";
import { Button } from "primereact/button";
import history from "../../history";
import { currenciesApiService } from "../../services/api/merchants/CurrenciesApiService";
import { isSuperAdmin } from "../../authGuard";

interface TransactionsProps {
    merchantBranch?: string;
    merchantId?: number;
    refreshTrigger?: number
}

type TransactionsState = {
    isLoading: boolean;
    merchants: SelectItem[];
    transactions: TransactionReportDetails[];
    filters?: unknown;
    viewTransactionDetails: boolean;
    selectedTransaction: TransactionReportDetails | null;
    first: number;
    rows: number;
    totalRecords: number;
    currencies: SelectItem[];
};

class AllTransactions extends Component<TransactionsProps, TransactionsState> {
    private dataTable: ExtendedDataTable | null = null;
    unlisten: (() => void) | null = null;
    constructor(props: TransactionsProps) {
        super(props);

        this.state = {
            isLoading: true,
            viewTransactionDetails: false,
            transactions: [],
            filters: {
                transactionID: { value: null, matchMode: FilterMatchMode.EQUALS },
                type: { value: null, matchMode: FilterMatchMode.EQUALS },
                timestampTo: { value: null, matchMode: FilterMatchMode.EQUALS },
                amount: { value: null, matchMode: FilterMatchMode.EQUALS },
                currency: { value: null, matchMode: FilterMatchMode.EQUALS },
            },
            selectedTransaction: null,
            first: 0,
            rows: 10,
            totalRecords: 0,
            merchants: [],
            currencies: [],
        };
    }

    componentDidMount(): void {
        this.onLoad();
    }

    componentWillUnmount() {
        // Clean up the history listener
        if (this.unlisten) {
            this.unlisten();
        }
    }
    componentDidUpdate(prevProps: TransactionsProps) {
        if (this.props.refreshTrigger !== prevProps.refreshTrigger) {
            this.onLoad();
        }
    }
    onLoad = async () => {
        await Promise.all([this.loadCurrencies(), this.loadTransactions()]);
        this.setState({ isLoading: false });
        // Listen for changes in the URL or query parameters
        this.unlisten = history.listen(async (location) => {
            if (location.action == "REPLACE" && location.location.pathname == "/all-transactions") {
                await this.handleSearchParams();
            }
        });
    };

    loadCurrencies = async () => {
        const currenciesResult = this.props.merchantId
            ? await currenciesApiService.getMerchantActiveCurrencies(this.props.merchantId)
            : await currenciesApiService.getAll();

        if (!currenciesResult.success) {
            this.setState({ isLoading: false });
            toastService.error(currenciesResult.message);
            return;
        }
        const currenciesList: SelectItem[] = currenciesResult.data.map((currency) => ({
            label: currency.charCode,
            value: currency.charCode,
        }));

        this.setState({ currencies: currenciesList });
    };

    onSorting = async (filters: SearchRequestBaseModel = {} as SearchRequestBaseModel, transactions?: TransactionReportDetails[]) => {
        this.setState({ transactions: await utils.extendedDatatableSorting(filters, transactions ?? this.state.transactions) });
    };

    handleSearchParams = async () => {
        const queryParams = new URLSearchParams(history.location.search);
        const getTransaction = queryParams.get("searchTransaction");
        if (getTransaction) {
            this.dataTable!.reset();
            const extractTransaction: TransactionReportDetails[] = (await JSON.parse(getTransaction)) as TransactionReportDetails[];
            this.setState({ transactions: extractTransaction || [] });
        }
    };

    loadTransactions = async (filters: SearchRequestBaseModel = {} as SearchRequestBaseModel) => {
        if (history.location.search == "" || filters.filters.length != 0) {
            history.location.search != "" ? history.replace({ pathname: "/all-transactions" }) : "";

            this.setState({ isLoading: true });
            const filterRequest: AllTransactionRequestModel = await utils.extractFiltersData(filters);
            filterRequest.dataBatch = filters.pageNumber ?? 1;
            filterRequest.dataBatchCount = filters.pageSize ?? 10;
            this.props.merchantBranch ? (filterRequest.merchant = this.props.merchantBranch) : null;
            const transactionsResult = await transactionsReportApiService.getAllTransactionsReportResponse(filterRequest);
            if (!transactionsResult.success) {
                this.setState({ isLoading: false, transactions: [] });
                toastService.error(transactionsResult.message);
                return;
            }
            await this.onSorting(filters, transactionsResult.data.transactions);
            this.setState({ isLoading: false, totalRecords: transactionsResult.data.transactionCount });
        }
    };

    renderStatusBody = (rowData: TransactionReportDetails) => {

        return RenderStatusBadge(rowData.transactionStatus);
    };

    renderHeader = (hasActiveFilters: boolean) => (
        <DataTableToolbar
            onClear={() => {
                if (isSuperAdmin()) {
                    history.replace({ pathname: "/all-transactions" });
                    this.dataTable!.reset();
                    return;
                }
                history.replace({ pathname: "/merchantdashboard" });
                this.dataTable!.reset();
            }}
            hasActiveFilters={hasActiveFilters}
        />
    );

    showTransactionDetails = (rowData: TransactionReportDetails) => {
        this.setState({ viewTransactionDetails: true, selectedTransaction: rowData });
    };

    onRowClick = (event: { data: TransactionReportDetails }) => {
        this.showTransactionDetails(event.data);
    };

    hideTransactionDetailsView = () => {
        this.setState({ viewTransactionDetails: false });
    };

    currencyFilterTemplate = (options: ColumnFilterElementTemplateOptions, columnName: string, placeHolder: string) => {
        return (
            <Dropdown
                value={options.value}
                options={this.state.currencies}
                filter
                onChange={(e) => options.filterCallback(e.value, options.index)}
                placeholder={`Select ${placeHolder}`}
                className="p-column-filter"
                showClear
            />
        );
    };

    render(): React.ReactNode {
        return (
            <>
                <AppCard className="mb-3">
                    {this.state.viewTransactionDetails && this.state.selectedTransaction != null && (
                        <TransactionDetailsDialog
                            isVisible={this.state.viewTransactionDetails}
                            onHide={this.hideTransactionDetailsView}
                            onRollbackSuccess={() => this.loadTransactions()}
                            transaction={this.state.selectedTransaction}
                            editable={false}
                        />
                    )}
                    <AppCard.Body className="!p-0">
                        <ExtendedDataTable
                            header={this.renderHeader}
                            value={this.state.transactions}
                            loading={this.state.isLoading}
                            totalRecords={this.state.totalRecords}
                            onChange={this.loadTransactions}
                            ref={(ref) => (this.dataTable = ref)}
                            defaultSortOrder={SortOrderType.Descending}
                            defaultSortField="timeStamp"
                            onSorting={this.onSorting}
                            onRowClick={this.onRowClick}
                        >
                            <Column
                                field="paymentSystem"
                                {...({ alwaysVisible: true } as any)}
                                header="Payment System"
                                body={(rowData: TransactionReportDetails) => <p>{rowData.paymentSystem}</p>}
                                showFilterMatchModes={false}
                                filterField="paymentSystem"
                                filterMatchModeOptions={stringFilterMatchModeOptions}
                                filterElement={TransactionSystemFilterTemplate}
                            />

                            <Column
                                field="transactionId"
                                header="Transaction ID"
                                {...({ alwaysVisible: true } as any)}
                                body={(rowData: TransactionReportDetails) => <p>{rowData.transactionId}</p>}
                                showFilterMatchModes={false}
                                filterField="transactionId"
                                filterPlaceholder="Search by Transaction ID"
                                filterMatchModeOptions={stringFilterMatchModeOptions}
                            />

                            <Column
                                field="transactionType"
                                header="Type"
                                {...({ alwaysVisible: true } as any)}
                                body={(rowData: TransactionReportDetails) => <p>{rowData.transactionType}</p>}
                                filterMatchModeOptions={stringFilterMatchModeOptions}
                                showFilterMatchModes={false}
                                filterField="trxDirection"
                                filterElement={TransactionTypeFilterTemplate}
                            />

                            <Column field="senderName" header="Sender" body={(rowData: TransactionReportDetails) => <p>{rowData.senderName}</p>} />

                            <Column
                                field="recepientName"
                                header="Recepient"
                                body={(rowData: TransactionReportDetails) => <p>{rowData.receiverName}</p>}
                            />

                            <Column
                                field="timeStamp"
                                header="Transaction Date"
                                body={dateBodyTemplate("timeStamp")}
                                dataType="date"
                                filterElement={dateRangeFilterTemplate}
                                filterField="timeStampTo"
                                filterMatchModeOptions={statusFilterMatchModeOptions}
                                showFilterMatchModes={false}
                            />

                            <Column
                                field="amount"
                                header="Amount"
                                showFilterMatchModes={false}
                                filterField="amountTo"
                                filterPlaceholder="Search by Amount"
                                filterMatchModeOptions={stringFilterMatchModeOptions}
                                body={(rowData: TransactionReportDetails) => <p>{utils.renderAmount(rowData.amount)}</p>} />

                            <Column
                                field="currency"
                                header="Currency"
                                body={(rowData: TransactionReportDetails) => <p>{rowData.currency}</p>}
                                showFilterMatchModes={false}
                                filterField="currency"
                                filterMatchModeOptions={stringFilterMatchModeOptions}
                                filterElement={(options) => this.currencyFilterTemplate(options, "currency", "Currency")}
                            />

                            <Column
                                field="status"
                                header="Status"
                                body={this.renderStatusBody}
                            />

                        </ExtendedDataTable>
                    </AppCard.Body>
                </AppCard>
            </>
        );
    }
}

export default AllTransactions;
